/**
 * Weather overlay — GoC MSC GeoMet WMS (Environment and Climate Change Canada).
 * Public, unauthenticated endpoint. Single-select raster overlay: Rain / Wind / Radar.
 * Mockup parity: fsl-mocks/fsl-rtm-map-mu.html `setWxLayer` (Leaflet `L.tileLayer.wms`) —
 * ported to Mapbox GL's raster-source `{bbox-epsg-3857}` WMS tiling pattern since GL has
 * no built-in WMS helper.
 */
import type { Map } from 'mapbox-gl';
import { type WxOverlay, useUiStore } from '@/lib/store/ui';

const SRC = 'src-weather';
const LAYER = 'lyr-weather';
const WMS_BASE = 'https://geo.weather.gc.ca/geomet';

// Layer IDs verified live against GeoMet's GetCapabilities — the mockup's
// `HRDPS.CONTINENTAL_WSPD_BAGL` / `RADAR_1KM_RDBR` no longer exist on the
// service (renamed/retired since the mockup was authored).
const WX_CONFIG: Record<
  Exclude<WxOverlay, 'none'>,
  { layer: string; opacity: number; intervalMins: number }
> = {
  rain: { layer: 'HRDPS.CONTINENTAL_PR', opacity: 0.75, intervalMins: 60 },
  wind: { layer: 'HRDPS.CONTINENTAL_WSPD', opacity: 0.75, intervalMins: 60 },
  radar: { layer: 'RADAR_1KM_RRAI', opacity: 0.8, intervalMins: 10 },
};

/** Step back from "now" to a timestamp GeoMet is guaranteed to have a frame for. */
function computeTime(kind: Exclude<WxOverlay, 'none'>): string {
  const d = new Date();
  d.setUTCSeconds(0, 0);
  if (kind === 'radar') {
    d.setUTCMinutes(Math.floor(d.getUTCMinutes() / 10) * 10 - 20);
  } else {
    d.setUTCMinutes(0);
    d.setUTCHours(d.getUTCHours() - 2);
  }
  return `${d.toISOString().slice(0, 19)}Z`;
}

function buildTileUrl(kind: Exclude<WxOverlay, 'none'>): string {
  const cfg = WX_CONFIG[kind];
  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetMap',
    LAYERS: cfg.layer,
    STYLES: '',
    CRS: 'EPSG:3857',
    WIDTH: '256',
    HEIGHT: '256',
    FORMAT: 'image/png',
    TRANSPARENT: 'true',
    TIME: computeTime(kind),
  });
  return `${WMS_BASE}?${params.toString()}&BBOX={bbox-epsg-3857}`;
}

function removeWeatherLayer(map: Map) {
  if (map.getLayer(LAYER)) map.removeLayer(LAYER);
  if (map.getSource(SRC)) map.removeSource(SRC);
}

/**
 * WMS layer + TIME window differ per selection, so the source is torn down and
 * rebuilt on every change rather than mutated in place (raster sources don't
 * expose a `setTiles`-style partial update in mapbox-gl-js).
 */
function syncWeatherLayer(map: Map) {
  const kind = useUiStore.getState().wxOverlay;
  removeWeatherLayer(map);
  if (kind === 'none') return;
  const cfg = WX_CONFIG[kind];
  map.addSource(SRC, {
    type: 'raster',
    tiles: [buildTileUrl(kind)],
    tileSize: 256,
    attribution: '&copy; Environment and Climate Change Canada / MSC GeoMet',
  });
  map.addLayer({
    id: LAYER,
    type: 'raster',
    source: SRC,
    paint: { 'raster-opacity': cfg.opacity },
  });
}

export function subscribeWeatherLayer(map: Map): () => void {
  syncWeatherLayer(map);
  const unsub = useUiStore.subscribe(
    (s) => s.wxOverlay,
    () => syncWeatherLayer(map),
  );
  return () => {
    unsub();
    removeWeatherLayer(map);
  };
}
